import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import { focusServices, tempoEvents, siteConfig } from '../data/mock';
import { Wine, Image, Users, Trophy, Mic, Gift, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const SERVICE_LANDING_LINKS = {
  'Moments Festifs': '/afterwork',
  'Team Building': '/team-building',
  'Challenges & Ateliers': '/seminaire-entreprise',
};

// Service Card Component with optional image carousel
const ServiceCard = ({ service, getIcon }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const hasImages = service.images && service.images.length > 0;
  const landingPath = SERVICE_LANDING_LINKS[service.title];

  useEffect(() => {
    if (hasImages && service.images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % service.images.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [hasImages, service.images]);

  const getImageData = (img) => {
    if (typeof img === 'string') {
      return { url: img, position: 'center' };
    }
    return { url: img.url, position: img.position || 'center' };
  };

  return (
    <div className="bg-white/80 rounded-md border border-brand-petrol/8 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col">
      {hasImages && (
        <div className="relative h-40 md:h-48 overflow-hidden">
          {service.images.map((img, imgIndex) => {
            const imgData = getImageData(img);
            return (
              <div
                key={imgIndex}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  imgIndex === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={imgData.url}
                  alt={service.title}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: imgData.position }}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            );
          })}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-3 right-3 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
            {getIcon(service.icon, "w-5 h-5 text-[#96452f]")}
          </div>
          {service.images.length > 1 && (
            <div className="absolute bottom-3 left-3 flex gap-1.5">
              {service.images.map((_, dotIndex) => (
                <div
                  key={dotIndex}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    dotIndex === currentImageIndex ? 'bg-white w-4' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className={`p-5 md:p-6 text-center flex-1 flex flex-col ${!hasImages ? 'pt-6 md:pt-8' : ''}`}>
        {!hasImages && (
          <div className="flex justify-center mb-4">
            {getIcon(service.icon, "w-10 h-10 md:w-12 md:h-12 text-[#96452f]")}
          </div>
        )}
        <h3 className="text-lg md:text-xl font-display text-brand-petrol mb-3">
          {service.title}
        </h3>
        <ul className="space-y-1.5 mb-4">
          {service.items.map((item, idx) => (
            <li key={idx} className="text-brand-petrol/70 text-sm">
              {item}
            </li>
          ))}
        </ul>
        {landingPath && (
          <Link
            to={landingPath}
            className="mt-auto inline-block text-sm font-medium text-brand-terracotta hover:text-[#7a3826] transition-colors"
          >
            En savoir plus →
          </Link>
        )}
      </div>
    </div>
  );
};

const NotreOffrePage = () => {
  const getIcon = (iconName, className = "w-12 h-12 text-[#96452f]") => {
    const props = { className, strokeWidth: 1.25 };
    switch (iconName) {
      case 'glass': return <Wine {...props} />;
      case 'gallery': return <Image {...props} />;
      case 'team': return <Users {...props} />;
      case 'challenge': return <Trophy {...props} />;
      case 'artiste': return <Mic {...props} />;
      case 'noel': return <Gift {...props} />;
      default: return null;
    }
  };

  return (
    <div>
      <SEO
        title="Afterworks, team building, séminaires & animations"
        description="Découvrez nos prestations événementielles : afterworks, team building, séminaires, marchés de Noël, challenges sportifs, ateliers créatifs et bien plus. Une offre sur mesure pour vos événements d'entreprise."
        keywords="offre événementielle, team building Lyon, afterwork entreprise, marché de Noël entreprise, challenge sportif, atelier culinaire, animation entreprise, séminaire"
        url="/notre-offre"
        image="/img/photo-focus.webp"
      />
      <PageHero
        title="Notre Offre"
        subtitle="Une boule à facette d'idées"
        backgroundImage="/img/photo-focus.webp"
      />

      {/* Focus Section - Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-[#D08154] mb-2">
                CRÉATEUR DE LIENS <span className="text-lg font-medium">(ET BRISEUR DE GLACE)</span>
              </h2>
              <h3 className="text-lg text-[#96452f] font-display italic mb-6">
                Et agitateur en entreprise
              </h3>
              <p className="text-brand-petrol/70 leading-relaxed mb-4">
                Fini les conversations gênantes sur la météo à la machine à café ! Notre mission ? Transformer vos collègues en véritable équipe. Nous ne créons pas seulement des événements, nous fabriquons des prétextes pour mieux se connaître, rire ensemble et libérer la créativité.
              </p>
              <p className="text-brand-petrol/70 leading-relaxed mb-4">
                Parce qu'aujourd'hui, le bureau est bien plus qu'un lieu de travail : c'est un lieu de vie, et il mérite d'être animé.
              </p>
              <p className="text-brand-petrol/70 leading-relaxed">
                TPE familiale ou multinationale ? Peu importe la taille, seule l'envie compte. Du budget serré aux idées les plus folles, nous adaptons le format à vos contraintes et à vos lieux. Prêts à secouer le quotidien ?
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/img/photo-focus-01.webp"
                  alt="Focus sur l'offre"
                  className="w-full h-auto"
                 loading="lazy" decoding="async" />
              </div>
              <img
                src={siteConfig.moonUrl}
                alt=""
                className="absolute -top-8 -right-8 w-20 h-20 opacity-30"
               loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-20 bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-display text-brand-petrol text-center mb-2 md:mb-4">
            Nos services
          </h2>
          <p className="text-center text-[#96452f] font-display italic mb-8 md:mb-12 text-sm md:text-base">
            Des événements sur mesure pour votre entreprise
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {focusServices.map((service, index) => (
              <ServiceCard key={index} service={service} getIcon={getIcon} />
            ))}
          </div>
        </div>
      </section>

      {/* Organization Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="/img/photo-focus-02.webp"
                alt="Organisation d'événements"
                className="rounded-2xl shadow-2xl w-full"
               loading="lazy" decoding="async" />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-display text-brand-petrol mb-2">
                DE A À Z <span className="text-xl font-medium">(ET MÊME UN PEU PLUS)</span>
              </h2>
              <h3 className="text-lg text-[#96452f] font-display italic mb-6">
                Notre savoir-faire, ne rien vous laisser faire !
              </h3>
              <p className="text-brand-petrol/70 leading-relaxed mb-6">
                Quand nous débarquons dans votre entreprise, c'est pour vous simplifier la vie. Le plan est simple :
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#96452f] text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                  <p className="text-brand-petrol/70"><strong className="text-brand-petrol">On cadre :</strong> Thème, budget, lieu, animation.</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#D08154] text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                  <p className="text-brand-petrol/70"><strong className="text-brand-petrol">On gère :</strong> De la communication interne (teasers, invitations) à la gestion des inscriptions.</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#96452f] text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                  <p className="text-brand-petrol/70"><strong className="text-brand-petrol">Vous profitez :</strong> Le jour de l'événement, nous sommes partout à la fois pour que vous n'ayez à vous soucier de rien.</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tempo Section */}
      <section className="py-20 bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display text-brand-petrol mb-4">
              VOTRE CALENDRIER, VOS RÈGLES
            </h2>
            <p className="text-brand-petrol/70 max-w-3xl mx-auto mb-6">
              Une fête unique pour Noël ou des animations toute l'année pour booster le moral des troupes ? Ne choisissez pas au hasard. Mon Evénemenciel vous propose de bâtir un calendrier annuel qui ressemble vraiment à votre entreprise.
            </p>
            <p className="text-[#96452f] font-display italic mb-8">
              Ponctuel ou récurrent, à chacun sa formule magique !
            </p>
            <p className="text-brand-petrol/70 max-w-3xl mx-auto mb-8">
              Dites-nous quel est votre rythme, nous suivrons la cadence :
            </p>
          </div>
          
          {/* Frequency Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
              <div className="w-16 h-16 bg-[#96452f] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1x</span>
              </div>
              <h3 className="font-display text-brand-petrol mb-2">Une fois par mois</h3>
              <p className="text-brand-petrol/70 text-sm">
                Pour transformer le bureau en véritable lieu de vie.
              </p>
            </div>
            
            <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
              <div className="w-16 h-16 bg-[#D08154] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3x</span>
              </div>
              <h3 className="font-display text-brand-petrol mb-2">Une fois par trimestre</h3>
              <p className="text-brand-petrol/70 text-sm">
                Le bon équilibre pour célébrer les réussites et recharger les batteries.
              </p>
            </div>
            
            <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-display text-brand-petrol mb-2">Une fois... tout court !</h3>
              <p className="text-brand-petrol/70 text-sm">
                Une envie soudaine, une date anniversaire ? On répond présent pour l'événement unique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Events Carousel - Auto scrolling */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-display text-brand-petrol">
              Nos propositions d'événements
            </h2>
            <p className="text-base text-[#96452f] font-display italic mt-2">
              et bien d'autre encore...
            </p>
          </div>

          {/* Auto-scrolling carousel */}
          <div className="relative">
            <div 
              className="flex gap-6 animate-scroll-carousel"
              style={{
                width: 'max-content',
              }}
            >
              {[...tempoEvents, ...tempoEvents].map((event, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-72"
                >
                  <div className="bg-brand-ivory rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 h-full">
                    {/* Image */}
                    <div className="relative h-40">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover"
                       loading="lazy" decoding="async" />
                      <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <Calendar className="w-4 h-4 text-[#96452f]" />
                        <span className="text-sm font-medium text-brand-petrol/80">
                          {event.month}
                        </span>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-lg font-display text-brand-petrol mb-2">
                        {event.title}
                      </h3>
                      <p className="text-brand-petrol/70 text-sm leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - Integrated Steps */}
      <section className="py-20 bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-display text-brand-petrol text-center mb-4">
            Quand Mon Evénemenciel arrive dans vos entreprises
          </h2>
          <p className="text-center text-[#96452f] font-display italic mb-16">
            Un accompagnement de A à Z
          </p>

          {/* Process Steps */}
          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#96452f] via-[#D08154] to-[#96452f] rounded-full" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1 */}
              <div className="relative group">
                <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-12 h-12 bg-[#96452f] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                    1
                  </div>
                  <div className="h-32 mb-4 rounded-xl overflow-hidden">
                    <img 
                      src="/img/photo-histoire.webp" 
                      alt="Définition du projet"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                     loading="lazy" decoding="async" />
                  </div>
                  <h3 className="text-lg font-display text-brand-petrol mb-2 text-center">
                    Définition du projet
                  </h3>
                  <ul className="text-sm text-brand-petrol/70 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#96452f]">•</span>
                      Type d'événement souhaité
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#96452f]">•</span>
                      Date et horaires
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#96452f]">•</span>
                      Budget prévisionnel
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group">
                <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-12 h-12 bg-[#D08154] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                    2
                  </div>
                  <div className="h-32 mb-4 rounded-xl overflow-hidden">
                    <img 
                      src="/img/photo-groupe.webp" 
                      alt="Découverte des lieux"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                     loading="lazy" decoding="async" />
                  </div>
                  <h3 className="text-lg font-display text-brand-petrol mb-2 text-center">
                    Préparation
                  </h3>
                  <ul className="text-sm text-brand-petrol/70 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#D08154]">•</span>
                      Découverte de vos lieux
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D08154]">•</span>
                      Nombre de participants
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D08154]">•</span>
                      Sélection du thème
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group">
                <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-12 h-12 bg-[#96452f] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                    3
                  </div>
                  <div className="h-32 mb-4 rounded-xl overflow-hidden">
                    <img 
                      src="/img/photo-tempo-septembre.jpg" 
                      alt="Catering et animation"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                     loading="lazy" decoding="async" />
                  </div>
                  <h3 className="text-lg font-display text-brand-petrol mb-2 text-center">
                    Organisation
                  </h3>
                  <ul className="text-sm text-brand-petrol/70 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#96452f]">•</span>
                      Proposition catering
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#96452f]">•</span>
                      Supports de communication
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#96452f]">•</span>
                      Gestion des invitations
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative group">
                <div className="bg-white/80 rounded-md p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                  <div className="w-12 h-12 bg-[#D08154] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                    4
                  </div>
                  <div className="h-32 mb-4 rounded-xl overflow-hidden">
                    <img 
                      src="/img/photo-tempo-decembre.jpg" 
                      alt="Jour J"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                     loading="lazy" decoding="async" />
                  </div>
                  <h3 className="text-lg font-display text-brand-petrol mb-2 text-center">
                    Jour J & Clôture
                  </h3>
                  <ul className="text-sm text-brand-petrol/70 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-[#D08154]">•</span>
                      Déroulé complet
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D08154]">•</span>
                      Animation sur place
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D08154]">•</span>
                      Bilan de l'événement
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom tagline */}
          <div className="text-center mt-12">
            <p className="text-xl text-[#96452f] font-bold">
              Notre savoir-faire, ne rien vous laisser faire !
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotreOffrePage;
